import { useEffect, useState } from "react";
import { useLocale } from "./i18n.tsx";

const CONSENT_KEY = "neckmoves:analytics-consent";
const VISITOR_KEY = "neckmoves:anonymous-id";
const OPEN_EVENT = "neckmoves:open-analytics-consent";

type Consent = "granted" | "denied";

function readConsent(): Consent | null {
  const value = window.localStorage.getItem(CONSENT_KEY);
  return value === "granted" || value === "denied" ? value : null;
}

function applyConsent(value: Consent) {
  window.localStorage.setItem(CONSENT_KEY, value);
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `neckmoves_analytics_consent=${value}; Path=/; Max-Age=31536000; SameSite=Lax${secure}`;
  if (value === "denied") window.localStorage.removeItem(VISITOR_KEY);
}

function visitorId() {
  const existing = window.localStorage.getItem(VISITOR_KEY);
  if (existing) return existing;
  const created = crypto.randomUUID();
  window.localStorage.setItem(VISITOR_KEY, created);
  return created;
}

export async function trackEvent(eventName: string, properties: Record<string, string | number | boolean | null> = {}) {
  if (readConsent() !== "granted") return;
  await fetch("/api/analytics/events", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      event_id: crypto.randomUUID(),
      event_name: eventName,
      anonymous_id: visitorId(),
      page: window.location.pathname,
      properties,
    }),
    keepalive: true,
  }).catch(() => undefined);
}

export async function identifyAnalytics() {
  if (readConsent() !== "granted") return;
  await fetch("/api/analytics/identify", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ anonymousId: visitorId() }),
    keepalive: true,
  }).catch(() => undefined);
}

export function openAnalyticsSettings() {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

export function AnalyticsConsent() {
  const { copy } = useLocale();
  const [consent, setConsent] = useState<Consent | null>(() => readConsent());
  useEffect(() => {
    if (consent === "granted") void trackEvent("page_view", { locale: document.documentElement.lang || "zh-CN" });
  }, [consent]);
  useEffect(() => {
    const open = () => setConsent(null);
    window.addEventListener(OPEN_EVENT, open);
    return () => window.removeEventListener(OPEN_EVENT, open);
  }, []);
  if (consent) return null;
  const choose = (value: Consent) => {
    applyConsent(value);
    setConsent(value);
  };
  return (
    <aside className="consent-card" aria-label={copy.consent.label}>
      <div>
        <strong>{copy.consent.title}</strong>
        <p>{copy.consent.body}</p>
      </div>
      <div className="consent-actions">
        <button className="button button-quiet" type="button" onClick={() => choose("denied")}>{copy.consent.necessary}</button>
        <button className="button button-primary" type="button" onClick={() => choose("granted")}>{copy.consent.allow}</button>
      </div>
    </aside>
  );
}
